import React, { useEffect, useState, useMemo } from 'react';
import {
  BoxStyled,
  GridStyled,
  BMRSection,
  BMRTitle,
  BMRValue,
  SubInfoStyled,
  TypographyStyled,
  InputBoxStyled,
  TypographyStyledHeader
} from './style';
import {
  setFluids,
  setFluidsRequire
} from '../../../store/slices/metaDataSlice';
import { useDispatch } from 'react-redux';
import { Tooltip } from './Tooltip';
import thermometerIcon from '../../../assets/icons/thermometerIcon.svg';
import waterDropIcon from '../../../assets/icons/waterDropIcon.svg';
import infoIcon from '../../../assets/icons/infoIcon.svg';
import { TabPanel } from './TabPanel';
import { PlaceholderGrid, CutOffIcon } from '../../../styles/globalStyles';
import { Button, Container, Modal } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import { roundToPrecision } from '../../../shared/utils/utilFunctions';

export const Fluids = () => {
  const {
    patientInformation,
    estimatingRequirements: { fluids },
    optedOptions: { routeArr }
  } = useAppSelector(metaDataSelector);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const weight =
    patientInformation.metric.estimatedDryWeight ||
    patientInformation.metric.weight;

  useEffect(() => {
    let fluidVal = 0;
    const age = patientInformation.age.years;
    if (age >= 18 && age <= 60) {
      fluidVal = 35 * weight;
    } else if (age > 60) {
      fluidVal = 30 * weight;
    }
    dispatch(
      setFluids({
        ...fluids,
        require1: fluidVal
      })
    );
  }, []);

  useEffect(() => {
    const temp = fluids.temperature - 37;
    const addFluids = fluids.additionalFluids;
    let fluidVal = 0;
    if (addFluids === 0) {
      fluidVal = temp * 2 * weight;
    } else if (addFluids === 1) {
      fluidVal = temp * 2.5 * weight;
    }
    dispatch(setFluidsRequire(fluidVal));
  }, [fluids.temperature, fluids.additionalFluids]);

  const getNextRoute = () => {
    if (routeArr.length) return routeArr[0];
    else return '/summary';
  };

  return (
    <>
      <BoxStyled>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '0 !important'
          }}
        >
          <TypographyStyledHeader>FLUID</TypographyStyledHeader>
          <Button
            onClick={() => handleOpen()}
            sx={{ height: '1.25rem', minWidth: '1.25rem', padding: 0 }}
          >
            <img src={infoIcon} alt={'infoIcon'} />
          </Button>
        </Container>
        <GridStyled container className="width-100">
          <BMRSection>
            <BMRTitle variant="h2">{'Requirement (mL):'}</BMRTitle>
            <BMRValue>
              {roundToPrecision(
                fluids.require1 + fluids.require2 + fluids.fluidLoss,
                1
              )}
            </BMRValue>
          </BMRSection>
          <BMRSection sx={{ marginTop: 0 }}>
            <BMRTitle variant="h2">{'Weight (kg):'}</BMRTitle>
            <BMRValue>{weight}</BMRValue>
          </BMRSection>
        </GridStyled>

        <CutOffIcon>
          <img src={thermometerIcon} alt="thermometerIcon" />
        </CutOffIcon>
        <PlaceholderGrid container width={'100%'}>
          <TypographyStyled variant="h3">TEMPERATURE</TypographyStyled>
          <InputBoxStyled>
            <SingleNumberCard
              width={'100%'}
              subHead={'(°C)'}
              range={{ min: 37, max: 60 }}
              value={fluids.temperature}
              setValue={(val: number) => {
                dispatch(setFluids({ ...fluids, temperature: Number(val) }));
              }}
            />
          </InputBoxStyled>
        </PlaceholderGrid>

        <CutOffIcon>
          <img src={waterDropIcon} alt="waterDropIcon" />
        </CutOffIcon>
        <PlaceholderGrid container width={'100%'}>
          <TypographyStyled variant="h3">ADDITIONAL FLUIDS</TypographyStyled>
          <SubInfoStyled sx={{ paddingBottom: '1.25rem' }}>
            Consider adding 2 – 2.5mL/kg for each °C rise in temperature above
            37°C.
          </SubInfoStyled>
          <TabPanel />
        </PlaceholderGrid>

        <CutOffIcon>
          <img src={waterDropIcon} alt="waterDropIcon" />
        </CutOffIcon>
        <PlaceholderGrid
          container
          width={'100%'}
          sx={{ marginBottom: '1.25rem' }}
        >
          <TypographyStyled variant="h3">
            ADJUST FOR FLUID LOSSES
          </TypographyStyled>
          <InputBoxStyled>
            <SingleNumberCard
              width={'100%'}
              subHead={'(mL)'}
              range={{ min: 0, max: 1000 }}
              value={fluids.fluidLoss}
              setValue={(val: number) => {
                dispatch(
                  setFluids({
                    ...fluids,
                    fluidLoss: Number(val)
                  })
                );
              }}
            />
          </InputBoxStyled>
          <SubInfoStyled sx={{ padding: '1.25rem 0' }}>
            Patients rarely need more than 3 litres of fluid per day. Assess on
            individual basis using clinical judgement.
          </SubInfoStyled>
        </PlaceholderGrid>
      </BoxStyled>
      <NavigationButtons nextDisabled={false} nextRoute={getNextRoute()} />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Tooltip onClose={handleClose} />
      </Modal>
    </>
  );
};
